import { useContext } from 'react';
import { ProgressBadge } from './ProgressBadge';
import { PhaseNumber, subphaseKey, useBusyKeys } from './phaseBusy';

// A page's section switcher, mirroring the reader's PhaseNav: one numbered card per
// section, click to switch, exactly one section shown at a time.
//
// How far along each section is, WHEN the caller knows — a `fraction` per item, drawn
// twice: as a ring around the number, and as green inside a common track below it. Every
// card is then drawn the same way and the only thing that differs between two of them is
// how much green is in them, which is the point: the switcher stops being a set of tabs
// where the one you are looking at happens to be green, and becomes a list of the work
// with your place in it marked by the card's own border.
//
// Without a fraction the bar is a static accent, filled on the active card and muted on
// the rest — which is what the Admin console and the catalog page want, having nothing to
// be partway through. It lives here rather than under panels/admin because it is no page's
// property: it takes labels and an id and knows nothing of what it switches.
export function SectionNav({
  items,
  active,
  onSelect,
}: {
  // `fraction` is 0..1 and optional per item. Give it to all of them or to none: a row
  // where one bar means completion and the next means selection reads as neither.
  // `busy`: the section's work is running — its bar and number hatched with the phase bar's
  // moving stripes (see PhaseNav), over whatever it has finished.
  items: { id: string; label: string; fraction?: number; busy?: boolean }[];
  active: string;
  onSelect: (id: string) => void;
}) {
  // AND HATCHED WHILE A BADGE OF ITS OWN SPINS, inside a phase (see phaseBusy) — over whatever the
  // caller says of its own runs, which go on when the section is not the one open.
  const phase = useContext(PhaseNumber);
  const spinning = useBusyKeys();
  const busyOf = (item: { id: string; busy?: boolean }) =>
    !!item.busy || (phase != null && spinning.has(subphaseKey(phase, item.id)));
  return (
    // ONE CARD WIDTH, EVERYWHERE, centred: a label longer than it goes onto a second line. Cards
    // that shared the row stretched with whatever sat under them, so one page's selector was
    // compact under one section and a row of banners under the next.
    //
    // WRAPPED, TWO TO A ROW ON A PHONE: four cards in one row are wider than the screen, and the
    // last ones were off its edge with the page sliding sideways after them.
    <div className="flex flex-wrap items-stretch justify-center gap-3">
      {items.map((item, i) => {
        const on = item.id === active;
        const busy = busyOf(item);
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item.id)}
            aria-current={on ? 'page' : undefined}
            className={`flex w-40 shrink-0 flex-col max-sm:w-[calc(50%-0.375rem)] items-center gap-2 rounded-lg border bg-white px-4 py-2.5 text-sm font-medium shadow-sm transition-colors ${
              on
                // The phase bar's 2 px of blue — border plus a 1 px ring, which takes no room
                // and so moves nothing when the selection does. See PhaseNav.
                ? 'border-blue-500 text-blue-700 ring-1 ring-blue-500'
                : 'border-slate-300 text-slate-500 hover:border-slate-400 hover:bg-slate-100 hover:text-slate-800'
            }`}
          >
            {/* The number, ringed with the card's own progress when there is any to
                report. The disc goes NEUTRAL then, on every card including the open one:
                a green ring around a green disc says nothing, and the border and the
                label already carry the selection. With no fraction the disc keeps the
                old job of marking which section is open. */}
            <ProgressBadge
              fraction={item.fraction}
              busy={busy}
              badgeClass={
                item.fraction == null && on
                  ? 'bg-blue-500 text-white'
                  : 'bg-slate-300 text-slate-600'
              }
            >
              {i + 1}
            </ProgressBadge>
            {item.fraction == null ? (
              <div
                className={`h-2.5 w-full rounded-full ${
                  on ? 'bg-blue-500' : 'bg-slate-200'
                }`}
              />
            ) : (
              /* The same track on every card, whether or not it is the one open, with the
                 green measuring the work. A hair of width on anything above zero, so
                 "barely started" is visible as itself rather than as "nothing". */
              <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full rounded-full bg-blue-500 transition-[width] duration-300"
                  style={{
                    width: `${Math.min(100, Math.max(item.fraction > 0 ? 4 : 0, item.fraction * 100))}%`,
                  }}
                />
                {/* THE HATCH OVER THE FILL, not under it: under it, a bar already full — a phase
                    being re-run — hid the whole of it, and a running section looked finished. */}
                {busy && <div className="phase-stripes absolute inset-0" />}
              </div>
            )}
            {/* THE LABEL CENTRED IN WHAT IS LEFT OF THE CARD: the cards of a row are one height,
                the tallest label's, so a one-line label sits between the two lines of its
                neighbour's rather than on the first of them. */}
            <span
              className={`flex flex-1 items-center justify-center text-balance whitespace-normal text-center leading-tight ${
                on ? 'text-blue-700' : 'text-slate-900'
              }`}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}
